import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RootErrorBoundary } from "./components/boundaries/RootErrorBoundary";
import { Bootstrap } from "./components/bootstrap/Bootstrap";
import "./index.css";

import { logger } from '@/lib/logging/Logger';
import { setupGlobalErrorHandler } from './utils/globalErrorHandler';
import './utils/diagnosticTesting'; // Enable diagnostic testing in development
import { storageHealthManager } from '@/lib/storage/StorageHealthManager';

// Initialize global error handling for production stability
setupGlobalErrorHandler();

// Run storage migration outside React lifecycle to prevent hook errors
(async () => {
  try {
    console.log('🔧 Starting pre-React storage fixes...');
    await storageHealthManager.initializeStorageHealth();
    
    // Emergency cleanup of problematic audit keys
    const problemKeys = ['adt-audit-trail', 'audit-trail'];
    for (const key of problemKeys) {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const size = item.length * 2;
          localStorage.removeItem(key);
          console.log(`🧹 Pre-React cleanup: ${key} (${Math.round(size / 1024)}KB)`);
        }
      } catch (error) {
        console.warn(`Failed to pre-cleanup ${key}:`, error);
      }
    }
    
    await storageHealthManager.emergencyCleanup();
    console.log('✅ Pre-React storage fixes complete');
  } catch (error) {
    console.error('❌ Pre-React storage fixes failed:', error);
  }
})();

// Production-ready initialization
logger.info('Application starting', { environment: process.env.NODE_ENV });

try {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <RootErrorBoundary>
        <Bootstrap />
      </RootErrorBoundary>
    </StrictMode>
  );
} catch (error) {
  logger.error('Critical error during app initialization', { error });
  
  // Fallback UI for critical bootstrap failures
  document.getElementById("root")!.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 20px;
      font-family: system-ui, -apple-system, sans-serif;
      background: #fef2f2;
      color: #dc2626;
    ">
      <h1 style="margin-bottom: 16px; font-size: 24px; font-weight: bold;">
        Critical Bootstrap Error
      </h1>
      <p style="margin-bottom: 16px; text-align: center; max-width: 500px;">
        Error: ${error instanceof Error ? error.message : 'Unknown error'}
      </p>
      <button 
        onclick="window.location.reload()" 
        style="
          padding: 12px 24px;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
        "
      >
        Reload Page
      </button>
    </div>
  `;
}